import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { usePageView } from "@/lib/analytics";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import SiteVerificationMount from "@/components/SiteVerificationMount";

// Route-level code splitting: keeps the initial bundle small so the storefront
// paints fast — admin pages and heavy libs (jspdf/html2canvas) load on demand.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Storefront = lazy(() => import("@/pages/Storefront"));
const ItemDetail = lazy(() => import("@/pages/ItemDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const MyBookings = lazy(() => import("@/pages/MyBookings"));
const PublicQuotation = lazy(() => import("@/pages/PublicQuotation"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Customers = lazy(() => import("@/pages/Customers"));
const Bookings = lazy(() => import("@/pages/Bookings"));
const BookingDetail = lazy(() => import("@/pages/BookingDetail"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const Users = lazy(() => import("@/pages/Users"));
const Categories = lazy(() => import("@/pages/Categories"));
const Settings = lazy(() => import("@/pages/Settings"));

const PageFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading…</div>
    </div>
);

const AdminProtected = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <PageFallback />;
    if (!user) return <Navigate to="/login?next=/admin" replace />;
    if (user.role !== "admin" && user.role !== "staff") return <Navigate to="/" replace />;
    return <Layout>{children}</Layout>;
};

const RedirectIfAuthed = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) {
        const dest = user.role === "admin" || user.role === "staff" ? "/admin" : "/";
        return <Navigate to={dest} replace />;
    }
    return children;
};

// Small component inside <BrowserRouter> so it can call usePageView (needs useLocation).
const AnalyticsTracker = () => {
    usePageView();
    return null;
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <CartProvider>
                    <WishlistProvider>
                        <BrowserRouter>
                            <AnalyticsTracker />
                            <SiteVerificationMount />
                            <Toaster position="top-right" richColors />
                            <Suspense fallback={<PageFallback />}>
                                <Routes>
                                    {/* Public storefront */}
                                    <Route path="/" element={<Storefront />} />
                                    <Route path="/shop" element={<Storefront browseOnly />} />
                                    <Route path="/shop/item/:id" element={<ItemDetail />} />
                                    <Route path="/cart" element={<Cart />} />
                                    <Route path="/checkout" element={<Checkout />} />
                                    <Route path="/my-bookings" element={<MyBookings />} />
                                    <Route path="/quotation/:id" element={<PublicQuotation />} />
                                    <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
                                    <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />

                                    {/* Admin */}
                                    <Route path="/admin" element={<AdminProtected><Dashboard /></AdminProtected>} />
                                    <Route path="/admin/inventory" element={<AdminProtected><Inventory /></AdminProtected>} />
                                    <Route path="/admin/customers" element={<AdminProtected><Customers /></AdminProtected>} />
                                    <Route path="/admin/bookings" element={<AdminProtected><Bookings /></AdminProtected>} />
                                    <Route path="/admin/bookings/:id" element={<AdminProtected><BookingDetail /></AdminProtected>} />
                                    <Route path="/admin/calendar" element={<AdminProtected><CalendarPage /></AdminProtected>} />
                                    <Route path="/admin/invoices" element={<AdminProtected><Invoices /></AdminProtected>} />
                                    <Route path="/admin/maintenance" element={<AdminProtected><Maintenance /></AdminProtected>} />
                                    <Route path="/admin/users" element={<AdminProtected><Users /></AdminProtected>} />
                                    <Route path="/admin/categories" element={<AdminProtected><Categories /></AdminProtected>} />
                                    <Route path="/admin/settings" element={<AdminProtected><Settings /></AdminProtected>} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </WishlistProvider>
                </CartProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
